import React, { useState, useRef, useEffect, useCallback } from 'react';
import { TableCell, Box, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import toast from 'react-hot-toast';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { CustomButton } from '../../Button';
import handleSubscribe from '../../../utils/getSubscription';
import { getStoreLogo, useUploadLogoMutation } from '../../../features/store';
import { StoreCellProps } from '../../../types';
import storeIconImage from '../../../assets/images/defaultStoreImage.svg';

const StoreCell: React.FC<StoreCellProps> = React.memo(({
  row,
  cellStyle,
  className = "store-name-container",
  onClick,
  linkTo,
  children,
}) => {
  const [isUploadingLogo, setIsUploadingLogo] = useState<boolean>(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoadingLogo, setIsLoadingLogo] = useState<boolean>(false);
  const [logoRefreshTrigger, setLogoRefreshTrigger] = useState<number>(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const uploadLogoMutation = useUploadLogoMutation();

  // Fetch store logo when component mounts or when logo URL changes
  useEffect(() => {
    const fetchLogo = async (): Promise<void> => {
      if (!row?.logo_image_url) {
        setLogoUrl(null);
        return;
      }

      try {
        setIsLoadingLogo(true);
        // Add cache-busting parameter to force fresh fetch
        const cacheBustUrl = `${row?.logo_image_url}${row?.logo_image_url?.includes('?') ? '&' : '?'}t=${Date.now()}`;

        // Use fetch directly for logo retrieval
        const response = await getStoreLogo(cacheBustUrl);
        console.log('StoreCell: Logo response:', response.data, typeof response.data);
        if (response.data instanceof Blob) {
          // Cleanup previous blob URL if it exists
          if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
          }

          const blobUrl = URL.createObjectURL(response.data);
          blobUrlRef.current = blobUrl;
          setLogoUrl(blobUrl);
          console.log('Logo fetched successfully for store:', row.id);
        } else {
          console.error("Response data is not a blob:", response.data);
          setLogoUrl(null);
        }
      } catch (error) {
        console.error('Error fetching store logo:', error);
        setLogoUrl(null);
      } finally {
        setIsLoadingLogo(false);
      }
    };

    fetchLogo();

    // Cleanup blob URL when component unmounts or logo changes
    return () => {
      if (blobUrlRef?.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [row?.logo_image_url, row?.id, row?.updated_at, logoRefreshTrigger]);

  const handleLogoUpload = useCallback((event: React.MouseEvent): void => {
    event.stopPropagation();
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      setIsUploadingLogo(true);
      await uploadLogoMutation.mutateAsync({ storeId: row.id, file });
      setLogoRefreshTrigger(prev => prev + 1); // Trigger logo refresh
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo. Please try again.');
    } finally {
      setIsUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [uploadLogoMutation, row?.id]);

  const handleSubscribeClick = useCallback((event: React.MouseEvent): void => {
    event.stopPropagation();
    handleSubscribe(row?.id);
  }, [row?.id]);

  const handleStoreImgClick = useCallback((event: React.MouseEvent): void => {
    event.stopPropagation();
  }, []);

  const isExpired = row?.cancellation_date ? new Date(row.cancellation_date) < new Date() : false;
  const trialStatusLabel = row?.is_credit_card === false
    ? `Expires In ${row?.remaining_days ?? 0} ${(row?.remaining_days ?? 0) === 1 ? 'Day' : 'Days'}`
    : 'Free Trial';

  const hasClassName = cellStyle && typeof cellStyle === 'object' && 'className' in cellStyle;
  const cellClassName = hasClassName ? (cellStyle as { className?: string }).className || className : className;
  const sx = hasClassName ? undefined : cellStyle;
  const storeDetailPath = linkTo;
  const showSubscribeButton = row?.subscription_status === "pending_activation" || isExpired;
  const subscriptionStatusContent = row?.cancellation_date ? (
    <div className={`${isExpired ? "store-expired-container" : "store-expires-container"} d-flex items-center justify-center`}>
      <p className="store-expiry-text">{isExpired ? "Cancelled" : "Expires on:"}</p>
      {!isExpired && <p className="store-expiry-date">{moment(row?.cancellation_date).format('MM/DD/YYYY')}</p>}
    </div>
  ) : row?.subscription_status === "active" ? (
    <p className="active-subscription-status">{row?.subscription_status}</p>
  ) : row?.subscription_status === "pending_cancel" ? (
    <p className="active-subscription-status">Active (Canceling)</p>
  ) : row?.subscription_status === "pending_activation" ? null
    : row?.subscription_status === "trial" ? (
      <p className="trial-subscription-status">{trialStatusLabel}</p>
    ) : row?.subscription_status === "past_due" ? (
      <p className="inactive-subscription-status">Payment Past Due</p>
    ) : row?.subscription_status === "canceled" ? (
      <p className="inactive-subscription-status">Canceled</p>
    ) : (
      <p className="inactive-subscription-status">Subscription Status Unavailable</p>
    );

  return (
    <TableCell className={cellClassName} sx={sx}>
      <Box className="store-column-container d-flex items-start justify-between" style={{
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <div className="d-flex items-center justify-between gap-12 width-100" style={{
          flex: 1,
          minWidth: 0, // Allow flex item to shrink below content size
          overflow: 'hidden'
        }}>
          <div className="store-icon-wrapper position-relative overflow-hidden" onClick={handleStoreImgClick}>
            <img
              src={logoUrl || storeIconImage}
              alt="Store"
              className="store-icon-image object-contain"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src = storeIconImage;
              }}
            />
            {isLoadingLogo && (
              <div className="position-absolute top-50 left-50" style={{ transform: 'translate(-50%, -50%)' }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
              </div>
            )}
            <div className="store-icon-overlay d-flex items-center justify-center" onClick={handleLogoUpload}>
              {isUploadingLogo ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              ) : (
                <EditIcon sx={{ color: 'white', fontSize: 16 }} />
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>

          <div className="store-name-container d-flex flex-column" style={{
            flex: 1,
            minWidth: 0, // Allow text to truncate
            overflow: 'hidden'
          }}>
            <div className="store-identity-name d-flex items-start justify-between gap-8" style={{ width: '100%' }}>
              {storeDetailPath ? (
                <Link to={storeDetailPath} className="store-table-link store-identity-link">
                  <p className="store-name" style={{
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal',
                    flex: 1,
                    minWidth: 0
                  }}>{row?.name}</p>
                </Link>
              ) : (
                <p className="store-name" onClick={onClick} style={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                  flex: 1,
                  minWidth: 0
                }}>{row?.name}</p>
              )}
            </div>
            <div className="store-address" style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              <div className="store-meta-row d-flex items-center gap-8">
                {storeDetailPath ? (
                  <Link to={storeDetailPath} className="store-table-link store-cell-meta-link">
                    <span className="store-address-text">{row?.city}, {row?.state}</span>
                    {subscriptionStatusContent && (
                      <div className='store-cell-subscription-container d-flex items-center flex-wrap'>
                        {subscriptionStatusContent}
                      </div>
                    )}
                  </Link>
                ) : (
                  <div className="store-cell-meta-link" onClick={onClick}>
                    <span className="store-address-text">{row?.city}, {row?.state}</span>
                    {subscriptionStatusContent && (
                      <div className='store-cell-subscription-container d-flex items-center flex-wrap'>
                        {subscriptionStatusContent}
                      </div>
                    )}
                  </div>
                )}
                {showSubscribeButton && (
                  <div className='store-cell-subscription-container d-flex items-center flex-wrap'>
                    <CustomButton className="button-base button-compact store-cell-subscribe-button" onClick={handleSubscribeClick}>
                      Subscribe
                    </CustomButton>
                  </div>
                )}
              </div>
            </div>
            {children}
            {/* <p className="store-address" onClick={onClick}></p> */}
          </div>
        </div>

      </Box>
    </TableCell>
  );
});

StoreCell.displayName = 'StoreCell';

export default StoreCell; 
